import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import { Select, Radio } from 'antd';
import type { SelectProps, SliderSingleProps, RadioChangeEvent } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { cardSelect } from '@entities/card';
import { TableParams } from '@types';
import { EAgePeriod } from '@utils';
import * as S from './styles';
import { API_DATE_FORMAT } from '@utils/const';

interface SelectOption {
    label: string;
    value: string;
}

interface IPropsFilterModal {
    tableParams: TableParams;
    setTableParams: (params: any) => void;
    modalVisibilityToggler: () => void;
}

const FilterModal: FC<IPropsFilterModal> = ({
    tableParams,
    setTableParams,
    modalVisibilityToggler,
}) => {
    const { filters } = tableParams;

    const [inputSlider, setInputSlider] = useState<number[]>(() =>
        filters?.age
            ? [Number(filters.age[0]), Number(filters.age[1])]
            : [EAgePeriod.START, EAgePeriod.END]
    );
    const [fromCreateDate, setFromCreateDate] = useState<string | null>(
        filters?.fromCreateDate ? filters?.fromCreateDate.toString() : null
    );
    const [toCreateDate, setToCreateDate] = useState<string | null>(
        filters?.toCreateDate ? filters?.toCreateDate.toString() : null
    );
    const [visit, setVisit] = useState<number | null>(() =>
        filters?.visit?.length === 1 ? Number(filters?.visit[0]) : null
    );

    const [gender, setGender] = useState<number | null>(() => {
        return filters?.sex?.length === 1 ? Number(filters.sex[0]) : null;
    });
    const [status, setStatus] = useState<number | null>(() => {
        return filters?.isActive?.length === 1
            ? Number(filters.isActive[0])
            : null;
    });

    const [selectedValues, setSelectedValues] = useState<string[]>(() =>
        filters?.templates ? filters.templates.map(String) : []
    );
    const { templates } = useSelector(cardSelect);

    const onSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setTableParams((prevState: TableParams) => {
            const newFilters = {
                ...prevState.filters,
                isActive: status !== null ? [status] : null,
                sex: gender !== null ? [gender] : null,
                visit: visit !== null ? [visit] : null,
                templates: selectedValues.map(Number),
                age:
                    inputSlider[0] !== EAgePeriod.START ||
                    inputSlider[1] !== EAgePeriod.END
                        ? inputSlider
                        : null,
                fromCreateDate: fromCreateDate ?? null,
                toCreateDate: toCreateDate ?? null,
            };
            return {
                ...prevState,
                filters: newFilters,
            };
        });

        modalVisibilityToggler();
    };

    const options: SelectProps['options'] = [
        { label: 'Выбрать все', value: '0' },
        ...(templates?.map(item => ({
            label: item.name,
            value: item.id.toString(),
        })) || []),
    ];

    const handleChange = (values: string[]) => {
        if (values.includes('0')) {
            if (!selectedValues.includes('0')) {
                setSelectedValues([
                    ...options
                        .filter((opt): opt is SelectOption => !!opt.value)
                        .map(opt => opt.value),
                ]);
            } else {
                setSelectedValues([]);
            }
        } else {
            setSelectedValues(values);
        }
    };

    const onCreateDateChange = (
        date: unknown,
        dateString: [string, string]
    ) => {
        if (date) {
            setFromCreateDate(
                dayjs(dateString[0]).format(API_DATE_FORMAT).valueOf()
            );
            setToCreateDate(
                dayjs(dateString[1]).format(API_DATE_FORMAT).valueOf()
            );
        }
    };

    const marks: SliderSingleProps['marks'] = {
        18: `${EAgePeriod.START} лет`,
        30: '30',
        50: '50',
        65: `${EAgePeriod.END} лет`,
    };

    const onChangeComplete = (value: number[]) => {
        setInputSlider(value);
    };

    const handleGenderChange = (e: RadioChangeEvent) => {
        setGender(e.target.value);
    };
    const handleStatusChange = (e: RadioChangeEvent) => {
        setStatus(e.target.value);
    };
    const handleVisitsChange = (e: RadioChangeEvent) => {
        setVisit(e.target.value);
    };

    return (
        <S.Wrapper>
            <S.HeaderTitle>
                <S.Title>Фильтр</S.Title>
                <S.CloseBtn onClick={modalVisibilityToggler}>
                    Закрыть
                </S.CloseBtn>
            </S.HeaderTitle>
            <S.FormContainer>
                <S.WrapBoxForm>
                    <S.FormItemsContainer>
                        <span>По дате создания</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <S.StyledRangePicker
                                    value={
                                        fromCreateDate && toCreateDate
                                            ? [
                                                  dayjs(fromCreateDate),
                                                  dayjs(toCreateDate),
                                              ]
                                            : null
                                    }
                                    format={[API_DATE_FORMAT, API_DATE_FORMAT]}
                                    onChange={onCreateDateChange}
                                />
                            </S.FormItem>
                            {fromCreateDate && toCreateDate && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        setFromCreateDate(null);
                                        setToCreateDate(null);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>

                        <span>По возрасту</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <S.StyledSlider
                                    range
                                    min={EAgePeriod.START}
                                    max={EAgePeriod.END}
                                    step={1}
                                    value={inputSlider}
                                    defaultValue={[
                                        EAgePeriod.START,
                                        EAgePeriod.END,
                                    ]}
                                    marks={marks}
                                    onChange={onChangeComplete}
                                    onChangeComplete={onChangeComplete}
                                />
                            </S.FormItem>
                            {(inputSlider[0] !== EAgePeriod.START ||
                                inputSlider[1] !== EAgePeriod.END) && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        onChangeComplete([
                                            EAgePeriod.START,
                                            EAgePeriod.END,
                                        ]);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>

                        <span>По полу</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleGenderChange}
                                    value={gender}
                                >
                                    <Radio value={1}>Мужской</Radio>
                                    <Radio value={2}>Женский</Radio>
                                </Radio.Group>
                            </S.FormItem>
                            {gender !== null && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        setGender(null);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>
                        <span>По макету</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <S.WrapSelect>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        style={{ width: '100%' }}
                                        maxTagCount={4}
                                        placeholder="Выбрать макеты"
                                        value={selectedValues}
                                        onChange={handleChange}
                                        options={options}
                                    />
                                </S.WrapSelect>
                            </S.FormItem>
                            {selectedValues.length > 0 && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        setSelectedValues([]);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>

                        <span>По статусу</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleStatusChange}
                                    value={status}
                                >
                                    <Radio value={1}>Активен</Radio>
                                    <Radio value={0}>Деактивирован</Radio>
                                </Radio.Group>
                            </S.FormItem>
                            {status !== null && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        setStatus(null);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>
                        <span>По посещениям</span>
                        <S.FormItemContainer>
                            <S.FormItem>
                                <Radio.Group
                                    onChange={handleVisitsChange}
                                    value={visit}
                                >
                                    <Radio value={1}>Посещал</Radio>
                                    <Radio value={0}>Не посещал</Radio>
                                </Radio.Group>
                            </S.FormItem>
                            {visit !== null && (
                                <DeleteOutlined
                                    style={{
                                        fontSize: '20px',
                                        color: '#a7388e',
                                    }}
                                    onClick={() => {
                                        setVisit(null);
                                    }}
                                />
                            )}
                        </S.FormItemContainer>
                    </S.FormItemsContainer>
                    <S.StyledFooter>
                        <S.StyledButtonSend onClick={onSubmit}>
                            <span>Применить фильтр</span>
                        </S.StyledButtonSend>
                    </S.StyledFooter>
                </S.WrapBoxForm>
            </S.FormContainer>
        </S.Wrapper>
    );
};

export default FilterModal;
